import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner } from 'react-bootstrap';
import { 
  FaRupeeSign, 
  FaArrowRight, 
  FaMobile, 
  FaJava, 
  FaReact, 
  FaPython, 
  FaDatabase,
  FaCode,
  FaLaptopCode,
  FaClock,
  FaUsers,
  FaGraduationCap
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PublicNavbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import CourseService from '../../services/courseService';
import '../../styles/pricing.css';

const Pricing = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await CourseService.getAllCourses();
      let coursesData = [];
      if (response && response.data && Array.isArray(response.data)) {
        coursesData = response.data;
      } else if (response && Array.isArray(response)) {
        coursesData = response;
      }
      // Sort by priority or id and take only top 6
      const sortedCourses = coursesData.sort((a, b) => (a.priority || 999) - (b.priority || 999));
      setCourses(sortedCourses.slice(0, 6));
    } catch (error) {
      console.error("Error loading courses:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN').format(price || 0);
  };

  const getCourseIcon = (courseName) => {
    const name = courseName.toLowerCase();
    if (name.includes('java')) return <FaJava className="course-icon-svg" />;
    if (name.includes('web') || name.includes('mern')) return <FaReact className="course-icon-svg" />;
    if (name.includes('full stack')) return <FaLaptopCode className="course-icon-svg" />;
    if (name.includes('game')) return <FaCode className="course-icon-svg" />;
    if (name.includes('python')) return <FaPython className="course-icon-svg" />;
    if (name.includes('data') || name.includes('database')) return <FaDatabase className="course-icon-svg" />;
    return <FaGraduationCap className="course-icon-svg" />;
  };

  const getCourseType = (courseName) => {
    const name = courseName.toLowerCase();
    if (name.includes('pre-recorded') || name.includes('self-paced')) return 'Self-Paced';
    if (name.includes('offline')) return 'Offline';
    return 'Online';
  };

  const getTypeBadgeVariant = (type) => {
    if (type === 'Self-Paced') return 'info';
    if (type === 'Offline') return 'success';
    return 'primary';
  };

  const formatStudentCount = (count) => {
    if (!count) return '0';
    if (count > 1000) return `${Math.floor(count / 1000)}k+`;
    return count;
  };

  return (
    <div className="pricing-page-wrapper">
      <PublicNavbar />

      {/* Hero Section with top spacing */}
      <div className="pricing-hero">
        <Container className="text-center">
          <div className="hero-badge">
            <span className="badge-text">Best Value Courses</span>
          </div>
          <h1 className="pricing-hero-title">
            Simple, Transparent <span className="text-gradient">Pricing</span>
          </h1>
          <p className="pricing-hero-subtitle">
            Choose from our industry-focused courses at affordable prices
          </p>
        </Container>
        <div className="pricing-hero-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" />
          </svg>
        </div>
      </div>

      {/* Pricing Cards */}
      <Container className="pricing-cards-container">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading courses...</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">No courses available at the moment.</p>
          </div>
        ) : (
          <>
            <Row className="g-4">
              {courses.map((course, index) => {
                const totalStudents = (course.base_student_count || 0) + (course.enrolled_students || 0);
                const courseType = getCourseType(course.name);
                
                return (
                  <Col lg={4} md={6} key={course.id}>
                    <Card className="pricing-card animate-card" style={{ animationDelay: `${index * 0.1}s` }}>
                      <Card.Body className="d-flex flex-column">
                        <div className="card-top">
                          <div className="course-type-badge">
                            <Badge bg={getTypeBadgeVariant(courseType)}>
                              {courseType}
                            </Badge>
                          </div>
                          <div className="course-icon-wrapper">
                            {getCourseIcon(course.name)}
                          </div>
                          <h3 className="course-title">{course.name}</h3>
                          
                          {/* Tags - All in one row */}
                          <div className="course-tags">
                            <span className="tag-item">
                              <FaClock className="tag-icon" />
                              {course.duration || 'Flexible'}
                            </span>
                            <span className="tag-item">
                              <FaUsers className="tag-icon" />
                              {formatStudentCount(totalStudents)} Students
                            </span>
                          </div>
                        </div>

                        {/* Price - Per Course */}
                        <div className="course-price-wrapper">
                          <div className="course-price">
                            <FaRupeeSign className="rupee-icon" />
                            {formatPrice(course.price || 0)}
                            <span className="price-period">/ course</span>
                          </div>
                        </div>

                        <p className="course-description">
                          {course.description?.substring(0, 100)}...
                        </p>

                        {/* Button at bottom - consistent alignment */}
                        <Button 
                          variant="primary"
                          className="pricing-enroll-btn mt-auto"
                          onClick={() => navigate(`/courses/${course.id}`)}
                        >
                          View Details <FaArrowRight className="ms-2" />
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          
            {/* Mobile App Note */}
            <div className="mobile-app-note">
              <div className="note-content">
                <FaMobile className="note-icon" />
                <div className="note-text">
                  <strong>Self-paced courses</strong> are available only on <strong>Sensible Academy Mobile App</strong>
                  <br />
                  <small>
                    Search <a href="https://play.google.com/store/apps/details?id=co.april2019.sens" target="_blank" rel="noopener noreferrer"><strong>Sensible Academy</strong></a> on Android and <a href="https://apps.apple.com/us/app/myinstitute/id1472483563" target="_blank" rel="noopener noreferrer"><strong>My Institute</strong></a> on iOS, 
                    then provide <strong>sens</strong> as organization code
                  </small>
                </div>
              </div>
            </div>
          </>
        )}
      </Container>

      {/* CTA Section */}
      <div className="pricing-cta-section">
        <Container className="text-center">
          <h2 className="pricing-cta-title">Need help choosing the right course?</h2>
          <p className="pricing-cta-text">
            Talk to our career counselors for personalized guidance
          </p>
          <Button 
            variant="light" 
            size="lg"
            className="pricing-cta-button"
            onClick={() => navigate('/contact')}
          >
            Talk to an Expert →
          </Button>
        </Container>
      </div>

      <Footer />
    </div>
  );
};

export default Pricing;